import React, { useState } from "react";
import { Building2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const RESELLER_IMG = "https://images.unsplash.com/photo-1516108317508-6788f6a160e4";

const Reseller = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/reseller", form);
      toast.success(t("reseller.success"));
      setForm({ name: "", email: "", phone: "", company: "", message: "", website: "" });
    } catch {
      toast.error("Error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const field = (key, type = "text") => (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t(`reseller.${key}`)}</label>
      <Input
        required={key === "name" || key === "email"}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`reseller-${key}`}
        className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal"
      />
    </div>
  );

  return (
    <div data-testid="reseller-page" className="grid grid-cols-1 lg:grid-cols-2 min-h-[calc(100vh-5rem)]">
      <div className="relative hidden lg:block">
        <img src={RESELLER_IMG} alt="Kaós reseller" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/30" />
      </div>
      <div className="flex flex-col justify-center px-6 md:px-16 py-16">
        <Reveal>
          <div className="w-11 h-11 border border-champagne rounded-full flex items-center justify-center mb-6">
            <Building2 size={20} strokeWidth={1.5} className="text-champagne" />
          </div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-charcoal">{t("reseller.title")}</h1>
          <p className="text-stone-500 mt-4 max-w-md">{t("reseller.subtitle")}</p>

          <form onSubmit={submit} className="mt-10 space-y-6 max-w-md">
            <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} tabIndex="-1" autoComplete="off" className="hidden" aria-hidden="true" />
            {field("name")}
            {field("email", "email")}
            {field("phone", "tel")}
            {field("company")}
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-stone-500">{t("reseller.message")}</label>
              <Textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="reseller-message"
                className="rounded-none border-0 border-b border-stone-300 bg-transparent px-0 mt-2 focus-visible:ring-0 focus-visible:border-charcoal resize-none"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="reseller-submit"
              className="rounded-none bg-charcoal text-white hover:bg-stone-800 h-12 px-10 text-xs uppercase tracking-[0.2em]"
            >
              {t("reseller.submit")}
            </Button>
          </form>
        </Reveal>
      </div>
    </div>
  );
};

export default Reseller;
